import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const logoutRef = useRef(null);

  // Logout function defined early and stable
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  }, []);

  // Store logout in ref for interceptor access
  logoutRef.current = logout;

  const fetchUser = useCallback(async () => {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      setLoading(false);
      setInitialized(true);
      return null;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching user:', error);
      // Only logout on explicit 401 Unauthorized, not on network errors
      if (error.response?.status === 401) {
        logout();
      }
      return null;
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, [logout]);

  // Setup axios interceptor once
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        // Only logout on 401 errors from auth-protected endpoints
        if (error.response?.status === 401 && 
            !error.config?.url?.includes('/auth/login') &&
            !error.config?.url?.includes('/game/players/login')) {
          if (logoutRef.current) {
            logoutRef.current();
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
      setInitialized(true);
    }
  }, [token, fetchUser]);

  const login = async (identifier, password) => {
    const response = await axios.post(`${API}/auth/login`, { identifier, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(newUser);
    
    return newUser;
  };

  // Derived state - computed from user object
  const isAuthenticated = !!user;
  const isAgency = user?.role === 'agency';
  const isTeamMember = user?.role === 'team_member';
  const isClient = user?.role === 'client';
  const isCustomerService = user?.role === 'customer_service';
  const isAffiliate = user?.role === 'affiliate' || (user?.role === 'team_member' && user?.team_member_type === 'affiliate');

  const value = {
    user,
    token,
    loading,
    initialized,
    login,
    register,
    logout,
    refreshUser: fetchUser,
    isAuthenticated,
    isAgency,
    isTeamMember,
    isClient,
    isCustomerService,
    isAffiliate,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
